// @ts-nocheck

import { cloneDeep, flatten, groupBy, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BoxV2, Checkbox, LoadingSkeleton, useDidUpdate, usePrevious } from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { ReportingInsightsDownload } from '@/modules/reporting/ReportingInsights/ReportingInsightsDownload';
import { ReportingMetricsAndKpisSelector } from '@/modules/reporting/ReportingMetricsAndKpisSelector';
import { getGroupOptions } from '@/modules/reporting/reporting.resources';
import { reportingInsightsSetMetricsConfig, reportingInsightsSetRawData } from '@/store/actions/reporting.actions';
import { getBaseCurrency } from '@/store/selectors/baseCurrency.selectors';
import {
  getCountriesWithEvents,
  getInsightsRawData,
  getReportingType,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import {
  getCountry,
  getCurrency,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { InsightsItemsSelector } from '@/ts-modules/reporting/InsightsItemsSelector';
import { SectionHeader } from '@/ts-modules/reporting/SectionHeader';
import ReportingInsightsItem from '../ReportingInsightsItem/ReportingInsightsItem';
import { checkIfDOOHAudienceStatsExists, formatFilters } from '../reporting.helpers';
import { InsightNavConfigStyled } from './ReportingInsightsGroupItem.style';

function ReportingInsightsGroupItem({ campaignInfo, config, country, filterSection, items, section }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const optionsShownItems = [5, 10];
  const reportingDOOHAudienceDataExistsToken = {};

  const reportingCurrency = useSelector(store => getCurrency(store, filterSection));
  const currencyInfo = useSelector(store => getBaseCurrency(store, reportingCurrency));
  const selectedCountry = useSelector(store => getCountry(store, filterSection));
  const staticMode = useSelector(getStaticMode);
  const rawData = useSelector(getInsightsRawData);
  const rawDataMode = rawData[section] || false;
  const countries = useSelector(getCountriesWithEvents);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const isDooh = isDOOHCampaign(useSelector(getReportingType));
  const variableType = useSelector(store => getVariableType(store, filterSection));
  const variableValues = useSelector(store => getVariableValues(store, filterSection));
  const downloadInsightsPermissions = checkUserResourcePermissions('reportingDownloadInsightsData', user, userRoles);
  const rankingItemsPermissions = checkUserResourcePermissions(
    'reportingInsightsRankingNumberOfItems',
    user,
    userRoles
  );
  const isMultiCountries = countries.length > 1;

  const [{ insightsConfigs, insightsItemsCanBeLoaded, metricsKpis, selectedItemsByFamily, shownItems }, setState] =
    useState({
      insightsConfigs: Object.entries(groupBy(config, 'type')).reduce(
        (prev, [family, familyItems]) => ({
          ...prev,
          [family]: {
            family,
            items: flatten(familyItems.map(i => i.items)).reduce(
              (prevFamilyItems, itemConfig) => ({
                ...prevFamilyItems,
                [itemConfig.id]: {
                  field: itemConfig.field,
                  groupOptions: itemConfig.groupsOptions,
                  id: itemConfig.id,
                  title: itemConfig.title
                }
              }),
              {}
            )
          }
        }),
        {}
      ),
      insightsItemsCanBeLoaded: Object.entries(groupBy(config, 'type')).reduce(
        (prev, [family, familyItems]) => ({
          ...prev,
          [family]: flatten(familyItems.map(i => i.items)).reduce(
            (prevFamilyItems, itemConfig) => ({
              ...prevFamilyItems,
              [itemConfig.field]: {
                insightsDataBeingChecked: false,
                insightsDataCanBeLoaded: true,
                insightsDataTotal: 0
              }
            }),
            {}
          )
        }),
        {}
      ),
      metricsKpis: {
        kpi: {
          id: '',
          label: ''
        },
        metric: {
          id: '',
          label: ''
        }
      },
      selectedItemsByFamily: config.reduce(
        (acc, { items: itemsTags, type }) => ({
          ...acc,
          ...(itemsTags.length > 0 && { [type]: itemsTags.map(({ id }) => id) })
        }),
        {}
      ),
      shownItems: 5
    });

  useEffect(() => {
    return () => {
      reportingDOOHAudienceDataExistsToken.cancel?.();
    };
  }, []);

  const prevMetric = usePrevious(metricsKpis);
  useDidUpdate(() => {
    if (prevMetric.metric.id !== metricsKpis.metric.id && isDooh) {
      (async () => {
        const DOOHAudienceStatsExists = await checkIfDOOHAudienceStatsExists(
          campaignInfo,
          config,
          metricsKpis.metric.id,
          selectedCountry,
          variableType,
          variableValues,
          reportingDOOHAudienceDataExistsToken
        );
        setState(prevState => ({
          ...prevState,
          insightsItemsCanBeLoaded: {
            ...prevState.insightsItemsCanBeLoaded,
            audience: config
              .find(({ type }) => type === 'audience')
              .items.reduce(
                (prevFamilyEntries, itemConfig) => ({
                  ...prevFamilyEntries,
                  [itemConfig.field]: {
                    insightsDataBeingChecked: false,
                    insightsDataCanBeLoaded: DOOHAudienceStatsExists.exists,
                    insightsDataTotal: DOOHAudienceStatsExists.total // todo this will be used when the disclaimer mode works
                  }
                }),
                {}
              )
          }
        }));
      })();
    }
  }, [metricsKpis]);

  const getDefaultFamilyItems = itemFamily => {
    return config.find(({ type }) => type === itemFamily).items.map(({ id }) => id);
  };

  const handleChange = (newItemsFamilies, isAllSelected) => {
    setState(prevState => ({
      ...prevState,
      selectedItemsByFamily: newItemsFamilies.reduce(
        (acc, itemFamily) => ({
          ...acc,
          [itemFamily]:
            !isAllSelected && selectedItemsByFamily[itemFamily]
              ? selectedItemsByFamily[itemFamily]
              : getDefaultFamilyItems(itemFamily)
        }),
        {}
      )
    }));
  };

  const selectedFamilyItems = useMemo(() => {
    return Object.entries(selectedItemsByFamily)
      .filter(([, familyItems]) => familyItems.length)
      .map(([family]) => family);
  }, [selectedItemsByFamily]);

  const handleItemsSelectionChange = newItemsByFamily =>
    setState(prevState => {
      return {
        ...prevState,
        selectedItemsByFamily: newItemsByFamily
      };
    });

  const onClickApplyButton = metricsKpisSelected => {
    setState(prevState => ({
      ...prevState,
      insightsItemsCanBeLoaded: {
        ...prevState.insightsItemsCanBeLoaded,
        audience: config
          .find(({ type }) => type === 'audience')
          .items.reduce(
            (prevFamilyEntries, itemConfig) => ({
              ...prevFamilyEntries,
              [itemConfig.field]: {
                insightsDataBeingChecked:
                  metricsKpisSelected?.metric?.id !== metricsKpis?.metric?.id
                    ? isDooh
                    : prevState.insightsItemsCanBeLoaded.audience[itemConfig.field].insightsDataBeingChecked,
                insightsDataCanBeLoaded: true,
                insightsDataTotal: 0
              }
            }),
            {}
          )
      },
      metricsKpis: metricsKpisSelected
    }));
    dispatch(
      reportingInsightsSetMetricsConfig({
        metricsConfig: metricsKpisSelected
      })
    );
  };

  const labelText = formatMessage({
    defaultMessage: 'Insights by',
    id: 'reporting.insights.selectorTitle'
  });

  const toggleRawData = useCallback(() => {
    dispatch(
      reportingInsightsSetRawData({
        section,
        state: !rawDataMode
      })
    );
  }, [rawDataMode]);

  const formatField = useCallback(
    (id, field) => {
      if (id === 'cartography' && isMultiCountries) {
        return 'cartoPyramid.level0';
      }
      return field;
    },
    [metricsKpis]
  );

  const formatGroupsOptions = useCallback(
    (id, groupsOptions) => {
      if (id === 'cartography' && isMultiCountries) {
        return [
          {
            ...groupsOptions[0],
            groupBy: 'cartoPyramid.level0'
          }
        ];
      }
      return groupsOptions;
    },
    [metricsKpis]
  );

  const onConfigChange = useCallback(
    (family, id, insightConfig) => {
      setState(prevState => {
        const insightsConfigsCopy = cloneDeep(prevState.insightsConfigs);
        insightsConfigsCopy[family].items[id] = {
          ...insightsConfigsCopy[family].items[id],
          ...insightConfig,
          ...(isEmpty(insightConfig.sortOptions) && {
            sortOptions: {
              metric: metricsKpis.kpi?.id,
              order: 'desc'
            }
          })
        };
        return {
          ...prevState,
          insightsConfigs: insightsConfigsCopy
        };
      });
    },
    [metricsKpis.kpi]
  );

  const insightsConfigsDataForDownload = useMemo(() => {
    return Object.values(insightsConfigs)
      .filter(({ family, items: familyItems, ...rest }) => {
        const familyHasActiveFilters = !isEmpty(selectedItemsByFamily[family]);
        const familyHasAtLeastOneItem = items.some(({ value }) => value === family);
        const familyHasAtLeastOneItemData = Object.values(familyItems).some(({ field }) => {
          const { insightsDataBeingChecked = false, insightsDataCanBeLoaded = true } =
            (insightsItemsCanBeLoaded && insightsItemsCanBeLoaded[family] && insightsItemsCanBeLoaded[family][field]) ||
            {};
          return !insightsDataBeingChecked && insightsDataCanBeLoaded;
        });
        return !!familyHasActiveFilters && familyHasAtLeastOneItem && familyHasAtLeastOneItemData;
      })
      .map(({ family, items: familyItems }) => ({
        family: formatMessage({
          defaultMessage: family,
          id: `reporting.insights.filters.${family}`
        }),
        items: Object.values(familyItems)
          .filter(({ id }) => selectedItemsByFamily[family].length && selectedItemsByFamily[family].includes(id))
          .map(({ groupOptions, id, title, ...rest }) => ({
            ...rest,
            groupOptions: getGroupOptions(groupOptions),
            item: formatMessage({
              defaultMessage: id,
              id: title
            })
          }))
      }));
  }, [selectedItemsByFamily, insightsConfigs, insightsItemsCanBeLoaded]);

  const handleOnClickOption = useCallback(event => {
    setState(prevState => ({
      ...prevState,
      shownItems: parseInt(event)
    }));
  });

  return (
    <>
      <div
        className={`reportingShortcuts__element--${campaignInfo?.cids?.length > 1 ? 'multi' : 'single'}`}
        id="reportingInsights"
      />
      <div className="reportingInsightsGroupItem__containerHeader">
        <SectionHeader
          {...(downloadInsightsPermissions && {
            downloadButton: (
              <ReportingInsightsDownload
                insightsConfig={insightsConfigsDataForDownload}
                metricsKpis={metricsKpis}
                section={filterSection}
              />
            )
          })}
          hideAdvancedFilters
          section="insights"
        >
          <div className="h2 fontRegular reportingInsightsGroupItem__title">
            {formatMessage({
              defaultMessage: 'Campaign insights',
              id: 'reporting.insights.title'
            })}
          </div>
        </SectionHeader>
      </div>
      <div className="reportingInsightsGroupItem boxPanel">
        <div className="reportingInsightsGroupItem__header">
          <ReportingMetricsAndKpisSelector
            hideKpis={isDooh}
            labelText={labelText}
            onClickApplyButton={onClickApplyButton}
          />
        </div>
        <InsightNavConfigStyled className="reportingInsightsGroupItem__config">
          <BoxV2 borderColor="tableBorderRow" id="reportingInsightsGroupItem-config-box">
            <BoxV2.Body className="reportingInsightsGroupItem__config-box" padding="24px">
              <div className="reportingInsightsGroupItem__config-element">
                <h4 className="reportingInsightsGroupItem__config-elementTitle">
                  {formatMessage({
                    defaultMessage: 'Category view setting',
                    id: 'reporting.insights.config.nav.title'
                  })}
                </h4>
                <div className="reportingInsightsGroupItem__config-elementContent">
                  <InsightsItemsSelector
                    className="reportingInsightsGroupItem__tagsContainer"
                    id="insights"
                    items={items}
                    onChangeToggled={handleChange}
                    selectedFamilyItems={selectedFamilyItems}
                    settingsModalButton={{
                      className: 'reportingInsightsGroupItem__selectionModalButton',
                      enabled: true,
                      onItemsSelectionModalToggled: handleItemsSelectionChange,
                      selectedItemsByFamily
                    }}
                  />
                </div>
              </div>
              <div className="reportingInsightsGroupItem__config-data">
                <div className="reportingInsightsGroupItem__config-element">
                  <h4 className="reportingInsightsGroupItem__config-elementTitle">
                    {formatMessage({
                      defaultMessage: 'Data view setting',
                      id: 'reporting.insights.config.data.settingsTitle'
                    })}
                  </h4>
                  <Checkbox
                    active={rawDataMode}
                    classes="reportingInsightsGroupItem__config-elementContent"
                    id="reportingInsightsGroupItem__rawDataSwitcher"
                    isSwitch
                    labelText={formatMessage({
                      defaultMessage: 'View raw data',
                      id: 'reporting.insights.rawDataSelector'
                    })}
                    onClickAction={toggleRawData}
                  />
                </div>
                {rankingItemsPermissions && !staticMode && (
                  <div className="reportingInsightsGroupItem__config-element">
                    <h4 className="reportingInsightsGroupItem__config-elementTitle">
                      {formatMessage({
                        defaultMessage: 'Ranking items',
                        id: 'reporting.insights.config.data.itemsTitle'
                      })}
                    </h4>
                    <div className="reportingInsightsGroupItem__config-elementContent">
                      {optionsShownItems.map(option => (
                        <Checkbox
                          active={option === shownItems}
                          id={option}
                          key={`reportingInsightsGroupItem__optionsToShow--${option}`}
                          labelText={option}
                          onClickAction={handleOnClickOption}
                          type="radio"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </BoxV2.Body>
          </BoxV2>
        </InsightNavConfigStyled>
        {metricsKpis.metric.id !== '' && (
          <div className="reportingInsightsGroupItem__content" id="reportingInsightsGroupItem__content">
            {config.map(row =>
              row.items
                .filter(({ id }) => selectedItemsByFamily[row.type] && selectedItemsByFamily[row.type].includes(id))
                .map((conf, index) => {
                  const { field, filters, groupsOptions, hideKpi, id, ...restConf } = conf;
                  const otherConfig = {
                    ...restConf,
                    ...insightsItemsCanBeLoaded[row.type][field]
                  };
                  return (
                    <ReportingInsightsItem
                      campaignInfo={campaignInfo}
                      country={country}
                      currencyInfo={currencyInfo}
                      family={row.type}
                      field={formatField(id, field)}
                      filters={formatFilters(metricsKpis, countries, filters || [], hideKpi)}
                      groupsOptions={formatGroupsOptions(id, groupsOptions)}
                      hideKpi={hideKpi}
                      id={id}
                      itemId={`ReportingInsightsItem-${conf.field}-${index}`}
                      key={`ReportingInsightsItem-${conf.field}-${index}`}
                      metricsKpis={metricsKpis}
                      onConfigChange={onConfigChange}
                      rawDataMode={{
                        force: rawDataMode,
                        hideCheckbox: true
                      }}
                      section={filterSection}
                      shownItems={shownItems}
                      {...otherConfig}
                    />
                  );
                })
            )}
          </div>
        )}
        {metricsKpis.metric.id === '' && (
          <LoadingSkeleton count={shownItems} delay={2} duration={2} height="200px" width="100%" />
        )}
      </div>
    </>
  );
}

ReportingInsightsGroupItem.propTypes = {
  campaignInfo: PropTypes.shape({
    cIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    cartographyFilter: PropTypes.shape({
      cartographyLevel: PropTypes.string,
      cartographyLevelIds: PropTypes.array
    }),
    currencyOptions: PropTypes.shape({
      baseCurrency: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired
    }),
    dateInterval: PropTypes.shape({
      beginDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      timezone: PropTypes.string.isRequired
    }),
    userId: PropTypes.string.isRequired
  }).isRequired,
  config: PropTypes.array.isRequired,
  country: PropTypes.string,
  filterSection: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  section: PropTypes.string.isRequired
};

export default React.memo(ReportingInsightsGroupItem);
